import { ArrowLeftOutlined, CheckOutlined, FileTextOutlined } from "@ant-design/icons";
import { Breadcrumb, Button, Empty, Flex, Input, Spin, Typography, message } from "antd";
import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAppSelector } from "../../hooks/redux-hooks";
import {
  type ComplianceTemplateSettings,
  useGetComplianceSettingsQuery,
  useSaveComplianceTemplateInstructionsMutation,
} from "../../service/compliance.service";

const PLACEHOLDER = `Example rules:
- Risk: If the clinician mentions suicidal ideation, document the Columbia Suicide Severity Rating Scale (C-SSRS) score and a safety plan.
- Screening: Document PHQ-9 score at least once every 90 days for patients with a depression diagnosis.
- Coding: CPT code 90837 requires documentation of 53+ minutes of psychotherapy.`;

const InstructionsPanel = ({ tmpl }: { tmpl: ComplianceTemplateSettings }) => {
  const [draft, setDraft] = useState(tmpl.instructions);
  const [save, { isLoading }] = useSaveComplianceTemplateInstructionsMutation();
  const isDirty = draft !== tmpl.instructions;

  const handleSave = async () => {
    try {
      await save({ noteId: tmpl.id, instructions: draft }).unwrap();
      message.success(`Saved instructions for "${tmpl.name}"`);
    } catch {
      message.error("Failed to save. Please try again.");
    }
  };

  return (
    <Flex vertical style={{ height: "100%" }}>
      <Flex justify="space-between" align="flex-start" style={{ marginBottom: 16 }}>
        <Flex vertical gap={2}>
          <Typography.Title level={5} style={{ margin: 0 }}>
            {tmpl.name}
          </Typography.Title>
          <Typography.Text style={{ color: "#6b7280", fontSize: 12 }}>
            Plain-English rules the AI agent checks every note against. One rule per line. Leave blank to skip
            compliance evaluation for this template.
          </Typography.Text>
        </Flex>
        <Button
          type="primary"
          icon={<CheckOutlined />}
          disabled={!isDirty}
          loading={isLoading}
          onClick={handleSave}
          style={{ flexShrink: 0, marginLeft: 16 }}
        >
          Save
        </Button>
      </Flex>
      <Input.TextArea
        key={tmpl.id}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder={PLACEHOLDER}
        style={{ fontFamily: "monospace", fontSize: 13, flex: 1, resize: "none", minHeight: 380 }}
      />
    </Flex>
  );
};

const ComplianceSettings = () => {
  const navigate = useNavigate();
  const { userInfo } = useAppSelector((state) => state.auth);
  const { data, isLoading } = useGetComplianceSettingsQuery();
  const [selectedId, setSelectedId] = useState<number | null>(null);

  if (!userInfo?.is_compliance_enabled || !userInfo?.is_group_admin) {
    return <Navigate to="/home" replace />;
  }

  const templates = data?.templates ?? [];
  const selected = templates.find((t) => t.id === selectedId) ?? templates[0] ?? null;

  return (
    <div style={{ backgroundColor: "#fff", minHeight: "100vh", textAlign: "left", padding: "40px" }}>
      <Flex align="center" gap={8} style={{ marginBottom: 28 }}>
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate("/compliance")}
          size="small"
          style={{ color: "#6b7280" }}
        />
        <Breadcrumb
          items={[
            {
              title: (
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate("/compliance");
                  }}
                >
                  Compliance
                </a>
              ),
            },
            { title: "Settings" },
          ]}
        />
      </Flex>

      <Flex vertical gap={4} style={{ marginBottom: 24 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          Compliance Settings
        </Typography.Title>
        <Typography.Text style={{ color: "#6b7280" }}>
          Select a custom note template to configure the compliance instructions the AI agent evaluates against.
        </Typography.Text>
      </Flex>

      {isLoading ? (
        <Flex justify="center" style={{ padding: 64 }}>
          <Spin />
        </Flex>
      ) : templates.length === 0 ? (
        <Empty
          description={
            <Flex vertical align="center" gap={8}>
              <Typography.Text>No group-shared templates found.</Typography.Text>
              <Typography.Text style={{ color: "#9ca3af", fontSize: 12 }}>
                Share a custom note template with your group from the Templates page, then return here to configure
                compliance instructions.
              </Typography.Text>
            </Flex>
          }
          style={{ padding: 64 }}
        />
      ) : (
        <div
          style={{
            display: "flex",
            gap: 0,
            border: "1px solid #e5e7eb",
            borderRadius: 8,
            overflow: "hidden",
            minHeight: 520,
          }}
        >
          {/* Left — template list */}
          <div
            style={{
              width: 280,
              flexShrink: 0,
              borderRight: "1px solid #e5e7eb",
              overflowY: "auto",
              background: "#fafafa",
            }}
          >
            {templates.map((tmpl) => {
              const isActive = (selected?.id ?? null) === tmpl.id;
              return (
                <button
                  key={tmpl.id}
                  type="button"
                  onClick={() => setSelectedId(tmpl.id)}
                  style={{
                    width: "100%",
                    padding: "14px 16px",
                    textAlign: "left",
                    background: isActive ? "#eff6ff" : "transparent",
                    borderTop: "none",
                    borderRight: "none",
                    borderBottom: "1px solid #e5e7eb",
                    borderLeft: `3px solid ${isActive ? "#4f46e5" : "transparent"}`,
                    cursor: "pointer",
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                  }}
                >
                  <Flex align="center" gap={6}>
                    <FileTextOutlined style={{ color: isActive ? "#4f46e5" : "#6b7280", fontSize: 13 }} />
                    <Typography.Text strong style={{ fontSize: 13, color: isActive ? "#312e81" : "#111827" }}>
                      {tmpl.name}
                    </Typography.Text>
                  </Flex>
                  {tmpl.document_preview ? (
                    <Typography.Text
                      style={{
                        fontSize: 11,
                        color: "#9ca3af",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        lineHeight: 1.5,
                        paddingLeft: 19,
                      }}
                    >
                      {tmpl.document_preview}
                    </Typography.Text>
                  ) : null}
                </button>
              );
            })}
          </div>

          {/* Right — instructions editor */}
          <div style={{ flex: 1, padding: 24, minWidth: 0 }}>
            {selected ? (
              <InstructionsPanel key={selected.id} tmpl={selected} />
            ) : (
              <Flex justify="center" align="center" style={{ height: "100%" }}>
                <Typography.Text style={{ color: "#9ca3af" }}>Select a template to configure.</Typography.Text>
              </Flex>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ComplianceSettings;
